import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../utils/api";
import {
  buildOperationalSummaryMetrics,
  getStartHourBucketLocal,
} from "./operationalAnalysisMetrics";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

const REPORT_LIMIT = 500;
const tableHeaderBg = "#1e293b";
const tableHeaderColor = "#fff";
const accent = "#0d9488";

function formatDateOnly(d) {
  if (!d) return "—";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function formatDuration(seconds) {
  if (seconds == null || isNaN(Number(seconds))) return "—";
  const s = Number(seconds);
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function sortHistoryByDate(data) {
  if (!Array.isArray(data) || data.length === 0) return data;
  return [...data].sort((a, b) => {
    const timeA = new Date(a.date || a.createdAt || a.disinfectionStartTime || 0).getTime();
    const timeB = new Date(b.date || b.createdAt || b.disinfectionStartTime || 0).getTime();
    return timeB - timeA;
  });
}

function computeReportData(history) {
  if (!Array.isArray(history) || history.length === 0) {
    return {
      trialStart: null,
      trialEnd: null,
      activeDays: 0,
      totalCycles: 0,
      totalUvcHours: 0,
      avgCyclesPerDay: 0,
      avgUvcPerDay: 0,
      nightSharePct: 0,
      completedCount: 0,
      totalUvcHoursCompleted: 0,
      activeDaysCompleted: 0,
      avgUniqueRoomsPerActiveDay: null,
      peakUniqueRooms: null,
      peakUniqueRoomsDate: null,
      nightShareCompletedPct: 0,
      areaBreakdown: [],
      dailyUvcHours: [],
      startTimeBuckets: [],
    };
  }
  const dates = new Set();
  let totalUvcSeconds = 0;
  let nightCount = 0;
  const areaMap = new Map();
  const dailyMap = new Map();
  const bucketCounts = { "Morning (07-12)": 0, "Afternoon (12-17)": 0, "Evening (17-19)": 0, "Night (19-24)": 0, "Late night (00-07)": 0 };

  history.forEach((row) => {
    const d = row.date || row.disinfectionStartTime || row.createdAt;
    if (d) dates.add(new Date(d).toDateString());
    const uvc = Number(row.uvLightUsageInSeconds ?? row.disinfectionTimeTakenSeconds ?? 0);
    totalUvcSeconds += uvc;
    const bucket = getStartHourBucketLocal(row.disinfectionStartTime || row.date || row.createdAt);
    if (bucket) {
      bucketCounts[bucket] = (bucketCounts[bucket] || 0) + 1;
      if (bucket === "Night (19-24)" || bucket === "Late night (00-07)") nightCount++;
    }
    const area = row.subLocation || row.map_name || "Other";
    if (!areaMap.has(area)) {
      areaMap.set(area, { area, cycles: 0, totalSeconds: 0, durations: [], nightCount: 0 });
    }
    const rec = areaMap.get(area);
    rec.cycles += 1;
    rec.totalSeconds += uvc;
    const dur = Number(row.disinfectionTimeTakenSeconds ?? row.uvLightUsageInSeconds ?? 0);
    rec.durations.push(dur);
    if (bucket === "Night (19-24)" || bucket === "Late night (00-07)") rec.nightCount += 1;
    const dayKey = d ? new Date(d).toDateString() : "";
    if (dayKey) {
      dailyMap.set(dayKey, (dailyMap.get(dayKey) || 0) + uvc / 3600);
    }
  });

  const instantFieldOrder = ["date", "disinfectionStartTime", "createdAt"];
  const summaryMetrics = buildOperationalSummaryMetrics(history, { instantFieldOrder });

  const activeDays = dates.size;
  const totalCycles = history.length;
  const totalUvcHours = totalUvcSeconds / 3600;
  const avgCyclesPerDay = activeDays > 0 ? totalCycles / activeDays : 0;
  const avgUvcPerDay = activeDays > 0 ? totalUvcHours / activeDays : 0;
  const nightSharePct = totalCycles > 0 ? Math.round((nightCount / totalCycles) * 100) : 0;

  const areaBreakdown = [...areaMap.entries()].map(([area, rec]) => {
    const avgMins = rec.durations.length ? rec.durations.reduce((a, b) => a + b, 0) / rec.durations.length / 60 : 0;
    const sorted = [...rec.durations].sort((a, b) => a - b);
    const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] / 60 : 0;
    return {
      areaType: area,
      cycles: rec.cycles,
      unique: rec.cycles,
      totalHours: Math.round((rec.totalSeconds / 3600) * 10) / 10,
      avgMins: Math.round(avgMins),
      median: Math.round(median),
      nightShare: rec.cycles ? Math.round((rec.nightCount / rec.cycles) * 100) + "%" : "0%",
    };
  }).sort((a, b) => b.cycles - a.cycles);

  const dailyUvcHours = [...dailyMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([dateStr, hours]) => ({ date: dateStr, hours: Math.round(hours * 10) / 10 }));

  const startTimeBuckets = Object.entries(bucketCounts).map(([name, count]) => ({ name, cycles: count }));

  return {
    ...summaryMetrics,
    activeDays,
    totalCycles,
    totalUvcHours,
    avgCyclesPerDay,
    avgUvcPerDay,
    nightSharePct,
    areaBreakdown,
    dailyUvcHours,
    startTimeBuckets,
  };
}

const MetricTable = ({ rows, sx = {} }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 1, overflow: "hidden", width: "100%", minWidth: 720, maxWidth: 840, ...sx }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600, width: "55%" }}>Metric</TableCell>
          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }}>Value</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(([metric, value], i) => (
          <TableRow key={i} sx={{ "&:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.02)" } }}>
            <TableCell sx={{ fontWeight: 500, color: "#475569" }}>{metric}</TableCell>
            <TableCell>{value ?? "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default function AnalysisRobotHistoryScreen() {
  const { robotId } = useParams();
  const navigate = useNavigate();
  const documentRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfRenderAllLogs, setPdfRenderAllLogs] = useState(false);

  useEffect(() => {
    if (!robotId) {
      setError("Robot ID is required");
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const fetchHistory = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await axios.get("/robot/history/fetch", {
          params: { robotId, page: 1, limit: REPORT_LIMIT },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const raw = Array.isArray(res.data.data) ? res.data.data : [];
        setHistory(sortHistoryByDate(raw));
        const summary = res.data.executiveSummary;
        setExecutiveSummary(
          Array.isArray(summary) ? summary.filter((s) => typeof s === "string" && s.trim()) : []
        );
      } catch (err) {
        if (controller.signal.aborted || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
          return;
        }
        console.error("Error fetching robot history:", err);
        setError(err.response?.data?.message || err.message || "Failed to load history.");
        setHistory([]);
        setExecutiveSummary([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchHistory();
    return () => controller.abort();
  }, [robotId]);

  const report = useMemo(() => computeReportData(history), [history]);
  const trialWindowStr =
    report.trialStart && report.trialEnd
      ? `${formatDateOnly(report.trialStart)} to ${formatDateOnly(report.trialEnd)}`
      : "—";
  const avgUvcPerActiveDayCompleted =
    report.activeDaysCompleted > 0
      ? (report.totalUvcHoursCompleted / report.activeDaysCompleted).toFixed(2)
      : "—";
  const peakRoomsStr =
    report.peakUniqueRooms != null && report.peakUniqueRoomsDate
      ? `${report.peakUniqueRooms} (on ${formatDateOnly(report.peakUniqueRoomsDate)})`
      : "—";
  const generatedDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const handleDownloadPdf = async () => {
    const element = documentRef.current;
    if (!element || history.length === 0) return;
    setPdfRenderAllLogs(true);
    setPdfDownloading(true);
    try {
      // Allow React to re-render the full table before capture.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        ignoreElements: (el) => el.classList?.contains("no-pdf-capture") === true,
      });
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 22;
      const headerHeight = 18;
      const footerHeight = 14;
      const contentX = margin;
      const contentY = margin + headerHeight;
      const contentWidth = pdfWidth - 2 * margin;
      const contentHeightPerPage = pdfHeight - 2 * margin - headerHeight - footerHeight;
      const imgHeightMm = (canvas.height * contentWidth) / canvas.width;
      const scaleY = imgHeightMm / canvas.height;
      const maxContentPx = contentHeightPerPage / scaleY;

      const drawPageFrame = (pageNum) => {
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.3);
        pdf.rect(margin, margin, pdfWidth - 2 * margin, pdfHeight - 2 * margin);
        pdf.setFillColor(13, 148, 136);
        pdf.rect(0, 0, pdfWidth, 8);
        pdf.setFontSize(14);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(30, 41, 59);
        pdf.text("Robot Disinfection Report", pdfWidth / 2, margin + 10, { align: "center" });
        const footerY = pdfHeight - margin - footerHeight;
        pdf.setDrawColor(100, 100, 100);
        pdf.line(margin, footerY + 2, pdfWidth - margin, footerY + 2);
        pdf.setFontSize(10);
        pdf.setFont(undefined, "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text("Page " + pageNum, pdfWidth / 2, pdfHeight - margin - 5, { align: "center" });
      };

      let pageNum = 1;
      let startPx = 0;
      while (startPx < canvas.height) {
        const endPx = Math.min(startPx + maxContentPx, canvas.height);
        const sliceHeightPx = endPx - startPx;
        const sliceHeightMm = sliceHeightPx * scaleY;
        drawPageFrame(pageNum);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, startPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
        pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", contentX, contentY, contentWidth, sliceHeightMm);
        startPx = endPx;
        pageNum++;
        if (startPx < canvas.height) pdf.addPage();
      }

      pdf.setTextColor(0, 0, 0);
      const safeName = (robotId || "robot").replace(/[^a-zA-Z0-9-_]/g, "_");
      pdf.save(`Robot-Disinfection-Report-${safeName}-${generatedDate.replace(/\s/g, "-").replace(/,/g, "")}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setPdfDownloading(false);
      setPdfRenderAllLogs(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column", px: { xs: 1.5, sm: 3 }, py: 0 }}>
      {/* Screen title (outside the document capture area) */}
      <Box
        className="no-pdf-capture"
        sx={{
          maxWidth: 900,
          mx: "auto",
          width: "100%",
          pt: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ textAlign: "center" }}>
          Robot Disinfection Report
        </Typography>
      </Box>

      {/* Actions (outside the document capture area) */}
      <Box
        className="no-pdf-capture"
        sx={{
          maxWidth: 900,
          mx: "auto",
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          pt: 1,
        }}
      >
        {history.length > 0 && (
          <Button
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={handleDownloadPdf}
            disabled={pdfDownloading}
            size="small"
            variant="outlined"
            sx={{ textTransform: "none", borderColor: accent, color: accent }}
          >
            {pdfDownloading ? "Generating…" : "Download PDF"}
          </Button>
        )}
      </Box>
      <Box ref={documentRef} sx={{ maxWidth: 900, mx: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Document header */}
        <Box
          component="header"
          sx={{
            py: 2,
            borderBottom: "2px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            px: 2,
            borderRadius: "0 0 8px 8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
            <IconButton
              className="no-pdf-capture"
              onClick={() => navigate("/analytics/robots-list")}
              size="small"
              sx={{ bgcolor: "grey.100", "&:hover": { bgcolor: "grey.200" } }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", textAlign: "center", mt: 1 }}
          >
            Generated: {generatedDate}
          </Typography>
        </Box>

        {loading && (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
          </Paper>
        )}

        {error && !loading && (
          <Paper variant="outlined" sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="body2" color="error.main">{error}</Typography>
          </Paper>
        )}

        {!loading && !error && history.length === 0 && (
          <Paper variant="outlined" sx={{ p: 8, textAlign: "center", borderRadius: 2 }}>
            <HistoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">No history for this robot.</Typography>
          </Paper>
        )}

        {!loading && !error && history.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, py: 3 }}>
            {/* Content: report intro and sections */}
            <Box component="main">
            {/* Report intro block */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" gutterBottom>
                About this report
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary", maxWidth: 720 }}>
                This document is generated from the disinfection history received from the robot. Each time the robot completes a cycle, it sends a record (start/end time, location, duration, UVC usage, battery, distance, etc.). The admin dashboard fetches this history and aggregates it here so you can monitor utilisation, plan staffing and subscription hours, and identify patterns (e.g. night-heavy runs, area-wise load). Use this report for operational review, capacity planning, and sharing with hospital or facility management.
              </Typography>
            </Paper>

            {/* 1. Operational Analysis */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                1. Operational Analysis of Disinfection Cycles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
                This section analyses the disinfection history received from the robot: only completed sessions are counted, and UVC exposure time is derived from the records (used for subscription or billable hours). The metrics below summarise how often the robot was used, over how many days, and how much UVC time was delivered in the selected period.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                <strong>What these metrics mean:</strong> Trial window is the date range of the received logs. Active days = distinct dates with at least one cycle. Total UVC time is the sum of UVC-on time across all cycles. Night share shows what proportion of cycles started after 7pm or before 7am — useful for understanding if usage is concentrated at night.
              </Typography>
              <MetricTable
                rows={[
                  ["Trial window (clean logs)", trialWindowStr],
                  ["Total completed disinfection cycles", String(report.completedCount)],
                  ["Total UVC exposure delivered (hours)", `${report.totalUvcHoursCompleted.toFixed(1)}`],
                  ["Active disinfection days", String(report.activeDaysCompleted)],
                  [
                    "Average unique rooms per active day",
                    report.avgUniqueRoomsPerActiveDay != null
                      ? report.avgUniqueRoomsPerActiveDay.toFixed(2)
                      : "—",
                  ],
                  ["Average UVC hours per active day", avgUvcPerActiveDayCompleted],
                  ["Peak unique rooms in a day", peakRoomsStr],
                  [
                    "Share of cycles starting after 7pm / before 7am",
                    `${report.nightShareCompletedPct}%`,
                  ],
                ]}
              />

              {report.areaBreakdown.length > 0 && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
                    Area-wise cycle time
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 720 }}>
                    The table below breaks down the received history by area (location/room type). Use it to see which zones consume the most cycles and UVC time, and how much of that runs at night. This helps with prioritising high-risk areas and planning Quick vs Deep mode.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 1, overflow: "hidden" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }}>Area type</TableCell>
                          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }} align="right">Cycles</TableCell>
                          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }} align="right">Total hrs</TableCell>
                          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }} align="right">Avg mins</TableCell>
                          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }} align="right">Median</TableCell>
                          <TableCell sx={{ bgcolor: tableHeaderBg, color: tableHeaderColor, fontWeight: 600 }} align="right">Night share</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.areaBreakdown.map((row, i) => (
                          <TableRow key={i} sx={{ "&:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.02)" } }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.areaType}</TableCell>
                            <TableCell align="right">{row.cycles}</TableCell>
                            <TableCell align="right">{row.totalHours}</TableCell>
                            <TableCell align="right">{row.avgMins}</TableCell>
                            <TableCell align="right">{row.median}</TableCell>
                            <TableCell align="right">{row.nightShare}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {report.areaBreakdown.length > 0 && (
                <Box sx={{ width: "100%", height: 280, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Average UVC minutes per cycle by area</Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.areaBreakdown.map((r) => ({ name: r.areaType, avgMins: r.avgMins }))} margin={{ top: 8, right: 8, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: "Average minutes (UVC on)", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                      <Tooltip />
                      <Bar dataKey="avgMins" fill={accent} name="Avg mins" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>Operational observations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, maxWidth: 720 }}>
                Based on the received history, the following patterns are typical:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary", fontSize: "0.875rem" }}>
                <li>Night-time bias in start times reflects current room/OT availability and sequencing with manual cleaning — a coordination constraint rather than a robot limitation.</li>
                <li>In many sites, manual cleaning is completed first and the robot is brought in afterwards. That avoids friction but can leave daytime turnover windows underused; Quick Mode during the day can add an extra hygiene layer without slowing turnaround.</li>
              </Box>
            </Paper>

            {/* 2. Executive summary */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                2. Executive Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
                The points below summarise what the received history shows. Use this section when briefing management or when deciding subscription tiers and staffing.
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  fontSize: "0.875rem",
                  color: "text.primary",
                  "& li": { mb: 0.75 },
                  "& b, & strong": { fontWeight: 700 },
                }}
              >
                {executiveSummary.length > 0 ? (
                  executiveSummary.map((html, i) => (
                    <Box
                      key={i}
                      component="li"
                      // Server-generated HTML from /robot/history/fetch (trusted backend).
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  ))
                ) : (
                  <>
                    <Box component="li">
                      In the period {trialWindowStr}, the robot reported {report.completedCount} completed disinfection cycles across {report.activeDaysCompleted} active days, totaling {report.totalUvcHoursCompleted.toFixed(1)} hours of UVC exposure time. This is the usage that drives subscription or billable hours.
                    </Box>
                    <Box component="li">
                      The operational window is currently night-heavy: {report.nightShareCompletedPct}% of completed cycles start after 7pm or before 7am (local time). That aligns with terminal cleaning after manual wash but means daytime turnover slots are often unused.
                    </Box>
                    <Box component="li">
                      On active days, average utilisation from completed cycles is {avgUvcPerActiveDayCompleted === "—" ? "—" : `${avgUvcPerActiveDayCompleted} hours`} of UVC per day and {report.avgUniqueRoomsPerActiveDay != null ? report.avgUniqueRoomsPerActiveDay.toFixed(2) : "—"} unique rooms per day. Peak days may be higher; the charts in sections 3 and 4 show the distribution.
                    </Box>
                    <Box component="li">
                      <strong>Key opportunity:</strong> Introducing Quick Mode (e.g. 15–20 minutes) during daytime turnover could reduce average cycle time and allow more cycles per month without increasing subscription hours — improving coverage in OTs, dialysis, and high-turnover areas.
                    </Box>
                  </>
                )}
              </Box>
            </Paper>

            {/* 3. Daily utilisation */}
            {report.dailyUvcHours.length > 0 && (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  3. Daily utilisation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 720 }}>
                  The chart below shows how many hours of UVC usage the robot reported for each day in the received history. Use it to spot busy vs quiet days, validate consistency of use, and align with staffing or room-availability patterns.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Daily UVC disinfection hours</Typography>
                <Box sx={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={report.dailyUvcHours} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: "Hours of UVC usage", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="hours" stroke={accent} strokeWidth={2} name="Hours" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            )}

            {/* 4. Start-time distribution */}
            {report.startTimeBuckets.some((b) => b.cycles > 0) && (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  4. Start-time distribution
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                  This chart shows when disinfection cycles started, based on the timestamps in the received history. Morning/Afternoon/Evening/Night/Late night buckets help you see whether usage is concentrated at night or spread across the day. A night-heavy pattern is common when the robot is used mainly for terminal cleaning; more daytime bars indicate use during turnover or quick cycles.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Disinfection start times (operational window)</Typography>
                <Box sx={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.startTimeBuckets} margin={{ top: 8, right: 8, left: 0, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: "Number of cycles", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                      <Tooltip />
                      <Bar dataKey="cycles" fill={accent} name="Cycles" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            )}

            {/* 5. Cycle log table */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                5. Cycle log (recent)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                This table lists the most recent disinfection records received from the robot. Each row is one completed cycle: date/time, mode, status, duration, location, battery use, and distance. Use it for audit, troubleshooting (e.g. partial runs or low battery), and to verify that the aggregated metrics above match the raw history.
              </Typography>
              <TableContainer sx={{ maxHeight: pdfRenderAllLogs ? "none" : 340, overflow: pdfRenderAllLogs ? "visible" : "auto" }}>
                <Table size="small" stickyHeader={!pdfRenderAllLogs}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>Mode</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }} align="right">Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }} align="right">Battery %</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }} align="right">Distance (m)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(pdfRenderAllLogs ? history : history.slice(0, 50)).map((row, index) => (
                      <TableRow key={row._id || index}>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>{formatDate(row.date)}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>{row.mode || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status || "—"}
                            color={row.status === "Completed" ? "success" : "warning"}
                            variant="outlined"
                            icon={row.status === "Completed" ? <CheckCircleOutlinedIcon /> : <WarningAmberOutlinedIcon />}
                            sx={{ fontSize: "0.75rem" }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.8125rem" }}>{formatDuration(row.disinfectionTimeTakenSeconds)}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>{row.subLocation || "—"}</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.8125rem" }}>{row.batteryUsageInPercentage ?? "—"}</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.8125rem" }}>{row.distanceTravelledInMeters ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!pdfRenderAllLogs && history.length > 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Showing latest 50 of {history.length} cycles.
                </Typography>
              )}
            </Paper>
            </Box>

            {/* Document footer */}
            <Box
              component="footer"
              sx={{
                mt: 4,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Prepared for Admin · Robot: {robotId || "—"} · Report generated {generatedDate}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                End of report
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
